import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getDevice, setDevice } from "@/lib/device";
import useScannerWedge from "@/lib/useScannerWedge";
import { useScale, scaleSupported, connectScale, disconnectScale } from "@/lib/scale";
import { useBankPos, bankPosSupported, connectBankPos, disconnectBankPos, chargeBankPos } from "@/lib/bankpos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Cpu, Printer, ScanLine, Tag, Save, Volume2, Usb, CheckCircle2, Info, MonitorSmartphone, Scale, CreditCard } from "lucide-react";

const TYPES = [
  ["kasa", "Kasa (POS)"],
  ["el_terminali", "El Terminali (mobil)"],
  ["depo", "Depo / Mal Kabul"],
  ["ofis", "Ofis / Yönetim"],
];

const SCALE_PROFILES = { generic: 9600, baster: 9600, desis: 9600, digi: 9600, mettler: 9600 };

const FLEET = [
  ["Kasa istasyonu", "2–3", MonitorSmartphone, "Her kasa kendi tarayıcısında çalışır; sepetler ve devam eden ödeme sunucuda saklanır."],
  ["Mobil el terminali", "2", Cpu, "Depo/mal kabulde okuttukça miktar otomatik artar (klavye modu)."],
  ["Kablosuz barkod okuyucu", "2", ScanLine, "Klavye emülasyonu — imleç boşken okutun, uygulama otomatik yakalar."],
  ["Fiş yazıcı (termal 80mm)", "2", Printer, "Satış sonrası tarayıcı yazdırma; sessiz baskı için kiosk/QZ Tray."],
  ["Barkod etiket yazıcı", "1", Tag, "Ürünler → Raf Etiketleri Yazdır ile etiket bas."],
];

function printHTML(title, body) {
  const w = window.open("", "_blank", "width=420,height=640");
  if (!w) { toast.error("Yazdırma penceresi açılamadı (popup engelli olabilir)"); return; }
  w.document.write(`<html><head><title>${title}</title><style>
    @page { size: 80mm auto; margin: 3mm; }
    body { font-family: 'Courier New', monospace; width: 72mm; margin: 0 auto; color:#000; }
    .c{text-align:center} .b{font-weight:800} .sm{font-size:11px}
    .hr{border-top:1px dashed #000; margin:6px 0}
    .logo{display:inline-block;border:2px solid #000;border-radius:6px;padding:2px 10px;font-weight:800;letter-spacing:1px}
    table{width:100%;font-size:12px} td{padding:1px 0} .r{text-align:right}
    .lbl{border:2px solid #000;border-radius:8px;padding:10px;text-align:center;margin-top:4px}
    .lbl .name{font-size:14px;font-weight:800} .lbl .price{font-size:28px;font-weight:900;margin:4px 0}
    .bars{font-size:36px;letter-spacing:-3px;line-height:1}
  </style></head><body>${body}<script>window.onload=function(){window.print();setTimeout(function(){window.close()},500)}<\/script></body></html>`);
  w.document.close();
}

const receiptHTML = (dev) => `
  <div class="c"><span class="logo">GROOT MARKET</span></div>
  <div class="c sm b" style="margin-top:4px">GROOT Toptan ve Market</div>
  <div class="c sm">${dev.name || "Kasa"} · TEST FİŞİ</div>
  <div class="hr"></div>
  <table>
    <tr><td>Elma (kg)</td><td class="r">1 x 24,90</td></tr>
    <tr><td>Ekmek</td><td class="r">2 x 7,50</td></tr>
    <tr><td>Süt 1L</td><td class="r">1 x 32,00</td></tr>
  </table>
  <div class="hr"></div>
  <table><tr><td class="b">TOPLAM</td><td class="r b">71,90 TL</td></tr></table>
  <div class="hr"></div>
  <div class="c sm">TEST çıktısı · ${new Date().toLocaleString("tr-TR")}</div>
  <div class="c sm b">Fiş yazıcı bağlantısı çalışıyor</div>`;

const labelHTML = () => `
  <div class="lbl">
    <div class="name">GROOT Örnek Ürün</div>
    <div class="sm">SKU: GRT-TEST-001</div>
    <div class="price">24,90 TL</div>
    <div class="sm">8690000000001</div>
    <div class="bars">| ||| | || |||| | ||</div>
    <div class="sm b">TEST ETİKETİ</div>
  </div>`;

const Row = ({ icon: Icon, title, desc, children }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3 last:border-0">
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 text-brand-green" />
      <div><p className="text-sm font-semibold text-slate-800">{title}</p><p className="text-[11px] text-slate-400">{desc}</p></div>
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default function DeviceSettings() {
  const [dev, setDev] = useState(() => ({ name: "", type: "kasa", autoPrintReceipt: true, autoPrintInvoice: true, scanBeep: true, scaleBaud: 9600, scaleProfile: "generic", bankPos: { enabled: false, brand: "generic", connection: "serial", baud: 9600, simulate: true }, ...getDevice() }));
  const scale = useScale();
  const bp = useBankPos();
  const [bpResult, setBpResult] = useState(null);
  const { can } = useAuth();
  const canConfig = can("config.manage");
  const [sbc, setSbc] = useState(null);
  useEffect(() => { if (canConfig) api.get("/settings/scale-barcode").then((r) => setSbc(r.data)).catch(() => {}); }, [canConfig]);
  const saveSbc = async () => { try { const { data } = await api.put("/settings/scale-barcode", sbc); setSbc(data); toast.success("Terazi barkodu ayarı kaydedildi"); } catch { toast.error("Kaydedilemedi (yetki gerekli)"); } };
  const sbcExample = () => {
    if (!sbc) return "";
    const plu = String(12).padStart(sbc.plu_len, "0");
    const w = String(1250).padStart(sbc.weight_len, "0");
    const arr = Array(13).fill("0");
    for (let i = 0; i < sbc.prefix.length; i++) arr[i] = sbc.prefix[i];
    for (let i = 0; i < sbc.plu_len; i++) arr[sbc.plu_start + i] = plu[i] || "0";
    for (let i = 0; i < sbc.weight_len; i++) arr[sbc.weight_start + i] = w[i] || "0";
    return arr.join("");
  };
  const updBp = (k, v) => setDev((d) => ({ ...d, bankPos: { ...(d.bankPos || {}), [k]: v } }));
  const testCharge = async () => {
    setBpResult(null);
    const res = await chargeBankPos({ amount: 1, config: dev.bankPos || {} });
    setBpResult(res);
    if (res.approved) toast.success(res.message); else toast.error(res.message);
  };
  const [lastScan, setLastScan] = useState(null);
  const [scanCount, setScanCount] = useState(0);
  useScannerWedge({ onScan: (c) => { setLastScan(c); setScanCount((n) => n + 1); }, enabled: true, beep: dev.scanBeep });

  const upd = (k, v) => setDev((d) => ({ ...d, [k]: v }));
  const save = () => { setDevice(dev); toast.success("Cihaz ayarları bu terminale kaydedildi"); };

  // --- Cihaz Politikası (sunucu, donanım — yetkiden AYRI) ---
  const [pol, setPol] = useState(null);
  const [polLoc, setPolLoc] = useState("");
  const [locs, setLocs] = useState([]);
  useEffect(() => {
    if (!canConfig) return;
    api.get("/settings/device-policy").then((r) => setPol(r.data)).catch(() => {});
    api.get("/locations").then((r) => { const arr = Array.isArray(r.data) ? r.data : (r.data.items || []); setLocs(arr.map((x) => ({ id: x.id || x._id, name: x.name }))); }).catch(() => {});
    /* eslint-disable-next-line */
  }, [canConfig]);
  const polEff = (k) => {
    if (!pol) return true;
    if (polLoc && pol.locations?.[polLoc] && k in pol.locations[polLoc]) return !!pol.locations[polLoc][k];
    return !!(pol.default || {})[k];
  };
  const setPolFlag = (k, v) => setPol((p) => {
    const np = { ...p, default: { ...(p.default || {}) }, locations: { ...(p.locations || {}) } };
    if (polLoc) np.locations[polLoc] = { ...(np.locations[polLoc] || {}), [k]: v };
    else np.default = { ...np.default, [k]: v };
    return np;
  });
  const savePol = async () => {
    try { const { data } = await api.put("/settings/device-policy", { default: pol.default, locations: pol.locations }); setPol(data); toast.success("Cihaz politikası kaydedildi"); }
    catch { toast.error("Kaydedilemedi (config.manage yetkisi gerekli)"); }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5 fade-in" data-testid="device-settings">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tight text-slate-900">
          <Cpu className="h-6 w-6 text-brand-green" /> Cihazlar & Yazıcılar
        </h1>
        <p className="mt-1 text-sm text-slate-500">Bu terminalin adını, yazıcı ve barkod okuyucu davranışını ayarlayın. Ayarlar bu cihazda saklanır.</p>
      </div>

      {canConfig && pol && (
        <div className="rounded-2xl border border-brand-green/30 bg-brand-green/5 p-5 shadow-sm" data-testid="device-policy-card">
          <div className="mb-1 flex items-center gap-2">
            <Cpu className="h-5 w-5 text-brand-green" />
            <p className="font-heading text-lg font-bold text-slate-900">Cihaz Politikası (Sunucu)</p>
          </div>
          <p className="mb-3 text-xs text-slate-500">Donanım davranışı — kullanıcı <b>yetkisinden</b> ve <b>ekran genişliğinden</b> bağımsızdır. Bir lokasyondaki tüm terminaller için merkezî geçerlidir; kasiyer yetkisi ne olursa olsun burada kapalı bir donanım POS'ta çalışmaz.</p>
          <div className="mb-3 flex items-center gap-2">
            <Label className="text-sm font-semibold text-slate-700">Kapsam</Label>
            <select data-testid="device-policy-scope" value={polLoc} onChange={(e) => setPolLoc(e.target.value)}
              className="h-9 rounded-md border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="">Varsayılan (tüm lokasyonlar)</option>
              {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="rounded-xl bg-white p-2 ring-1 ring-inset ring-slate-200">
            {(pol.fields || []).map((f) => (
              <Row key={f.key} icon={Printer} title={f.label} desc={f.hint}>
                <Switch data-testid={`device-policy-${f.key}`} checked={polEff(f.key)} onCheckedChange={(v) => setPolFlag(f.key, v)} />
              </Row>
            ))}
          </div>
          {polLoc && <p className="mt-2 text-[11px] text-amber-600">Bu lokasyona özel ayar giriyorsunuz — yalnızca değiştirdiğiniz anahtarlar bu lokasyon için geçerli olur, diğerleri varsayılanı kullanır.</p>}
          <div className="mt-3">
            <Button data-testid="device-policy-save" onClick={savePol} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-2 h-4 w-4" /> Politikayı Kaydet</Button>
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2 items-start">
      {/* Bu terminal */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="overline mb-3">Bu Terminal</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label htmlFor="dev-name" className="text-sm font-semibold text-slate-700">Terminal Adı</Label>
            <Input id="dev-name" data-testid="device-name" value={dev.name} onChange={(e) => upd("name", e.target.value)} placeholder="Örn: Kasa 1 / El Terminali 1" className="mt-1" />
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">Terminal Tipi</Label>
            <select data-testid="device-type" value={dev.type} onChange={(e) => upd("type", e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <Row icon={Printer} title="Satış sonrası otomatik fiş yazdır" desc="Kapalıysa fiş baskısı yapılmaz (yazıcısı olmayan istasyonlar için).">
            <Switch data-testid="device-autoprint" checked={dev.autoPrintReceipt} onCheckedChange={(v) => upd("autoPrintReceipt", v)} />
          </Row>
          <Row icon={Printer} title="Müşterili satışta otomatik A5 fatura yazdır" desc="Müşteri seçiliyse e-Fatura/e-Arşiv A5 formatında normal yazıcıdan basılır.">
            <Switch data-testid="device-autoprint-invoice" checked={dev.autoPrintInvoice} onCheckedChange={(v) => upd("autoPrintInvoice", v)} />
          </Row>
          <Row icon={Volume2} title="Barkod okuma sesi (beep)" desc="Her başarılı okumada kısa sesli uyarı.">
            <Switch data-testid="device-beep" checked={dev.scanBeep} onCheckedChange={(v) => upd("scanBeep", v)} />
          </Row>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="device-save" onClick={save} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-2 h-4 w-4" /> Kaydet</Button>
          <Button data-testid="test-receipt" onClick={() => printHTML("Test Fiş", receiptHTML(dev))} variant="outline" className="border-brand-green text-brand-green hover:bg-emerald-50"><Printer className="mr-2 h-4 w-4" /> Test Fiş Yazdır</Button>
          <Button data-testid="test-label" onClick={() => printHTML("Test Etiket", labelHTML())} variant="outline" className="border-brand-orange text-brand-orange hover:bg-orange-50"><Tag className="mr-2 h-4 w-4" /> Test Etiket Yazdır</Button>
        </div>
      </div>

      {/* Elektronik terazi */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="scale-settings">
        <p className="overline mb-3 flex items-center gap-1.5"><Scale className="h-4 w-4 text-brand-green" /> Elektronik Terazi (Canlı Tartı)</p>
        <p className="mb-3 text-xs text-slate-400">Kilolu ürünlerde ağırlık teraziden canlı okunur. Kasadaki tartma penceresi bu ayarlarla bağlanır; vardiya başında bir kez "Tartıya Bağlan" demeniz yeterlidir.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-sm font-semibold text-slate-700">Terazi Markası / Profili</Label>
            <select data-testid="scale-profile" value={dev.scaleProfile} onChange={(e) => { const p = e.target.value; upd("scaleProfile", p); if (SCALE_PROFILES[p]) upd("scaleBaud", SCALE_PROFILES[p]); }}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="generic">Genel (sürekli ASCII çıkış)</option>
              <option value="baster">Baster</option>
              <option value="desis">Desis</option>
              <option value="digi">Digi</option>
              <option value="mettler">Mettler Toledo</option>
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">Baud Hızı (bps)</Label>
            <select data-testid="scale-baud" value={dev.scaleBaud} onChange={(e) => upd("scaleBaud", Number(e.target.value))}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              {[2400, 4800, 9600, 19200, 38400, 115200].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4">
          {scaleSupported() ? (
            <div className={`flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2 text-sm ring-1 ring-inset ${scale.connected ? "bg-emerald-50 ring-emerald-200" : "bg-slate-50 ring-slate-200"}`}>
              <span className="inline-flex items-center gap-2 font-semibold">
                <Scale className="h-4 w-4" />
                {scale.connected
                  ? <span className="text-emerald-700">Bağlı · canlı ağırlık: <span data-testid="scale-live-weight" className="tabular">{scale.weight.toFixed(3)} kg</span>{scale.stable ? "" : " (ölçülüyor…)"}</span>
                  : <span className="text-slate-500">Tartı bağlı değil</span>}
              </span>
              {scale.connected ? (
                <Button data-testid="scale-test-disconnect" onClick={() => disconnectScale()} size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">Bağlantıyı Kes</Button>
              ) : (
                <Button data-testid="scale-test-connect" onClick={async () => { try { await connectScale(dev.scaleBaud); toast.success("Tartıya bağlanıldı"); } catch (e) { toast.error(e.message || "Tartı bağlanamadı"); } }} size="sm" className="bg-brand-green text-white hover:bg-brand-green-600"><Scale className="mr-1.5 h-4 w-4" /> Tartıya Bağlan (test)</Button>
              )}
            </div>
          ) : (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-700 ring-1 ring-inset ring-amber-200">Bu tarayıcı seri port (WebSerial) desteklemiyor. Canlı tartı için Chrome veya Edge kullanın; desteklenmeyen tarayıcılarda kilo manuel girilir.</p>
          )}
        </div>
      </div>

      {/* Terazi barkodu formatı (gömülü ağırlık) — yetkili kullanıcı */}
      {canConfig && sbc && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="scale-barcode-settings">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="overline flex items-center gap-1.5"><ScanLine className="h-4 w-4 text-brand-green" /> Terazi Barkodu Formatı</p>
              <p className="mt-0.5 text-xs text-slate-400">Gömülü ağırlıklı barkodların (marka bazlı) çözümleme formatı. Pozisyonlar 0'dan başlar. (Eskiden Sistem Ayarları'ndaydı.)</p>
            </div>
            <Switch data-testid="scale-enabled" checked={sbc.enabled} onCheckedChange={(v) => setSbc({ ...sbc, enabled: v })} />
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div><Label>Önek (prefix)</Label><Input data-testid="scale-prefix" value={sbc.prefix} onChange={(e) => setSbc({ ...sbc, prefix: e.target.value.replace(/\D/g, "") })} className="mt-1 font-mono" /></div>
            <div><Label>PLU başlangıç</Label><Input data-testid="scale-plu-start" type="number" value={sbc.plu_start} onChange={(e) => setSbc({ ...sbc, plu_start: +e.target.value })} className="mt-1" /></div>
            <div><Label>PLU uzunluk</Label><Input data-testid="scale-plu-len" type="number" value={sbc.plu_len} onChange={(e) => setSbc({ ...sbc, plu_len: +e.target.value })} className="mt-1" /></div>
            <div><Label>Ağırlık başlangıç</Label><Input data-testid="scale-w-start" type="number" value={sbc.weight_start} onChange={(e) => setSbc({ ...sbc, weight_start: +e.target.value })} className="mt-1" /></div>
            <div><Label>Ağırlık uzunluk</Label><Input data-testid="scale-w-len" type="number" value={sbc.weight_len} onChange={(e) => setSbc({ ...sbc, weight_len: +e.target.value })} className="mt-1" /></div>
            <div><Label>Bölen (gram→kg=1000)</Label><Input data-testid="scale-divisor" type="number" value={sbc.weight_divisor} onChange={(e) => setSbc({ ...sbc, weight_divisor: +e.target.value })} className="mt-1" /></div>
          </div>
          <div className="mt-4 rounded-lg bg-slate-50 p-3 text-sm">
            <span className="text-slate-500">Örnek barkod (PLU 12, 1250g): </span>
            <b className="font-mono text-brand-green" data-testid="scale-example">{sbcExample()}</b>
          </div>
          <Button data-testid="scale-save" onClick={saveSbc} className="mt-4 bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-2 h-4 w-4" /> Kaydet</Button>
        </div>
      )}

      {/* Banka POS cihazı */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="bankpos-settings">
        <div className="mb-2 flex items-center justify-between">
          <p className="overline flex items-center gap-1.5"><CreditCard className="h-4 w-4 text-brand-green" /> Banka POS Cihazı (Entegre / OPI)</p>
          <Switch data-testid="bankpos-enabled" checked={!!dev.bankPos?.enabled} onCheckedChange={(v) => updBp("enabled", v)} />
        </div>
        <p className="mb-3 text-xs text-slate-400">Açıkken kart ödemesinde tutar kasadaki banka POS cihazına otomatik gönderilir; onay/red kasaya döner. Gerçek cihaz kasa PC'sine seri (WebSerial) ile bağlanır; cihaz yokken Simülasyon modu ile test edilir.</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-sm font-semibold text-slate-700">Cihaz Markası / Profili</Label>
            <select data-testid="bankpos-brand" value={dev.bankPos?.brand || "generic"} onChange={(e) => updBp("brand", e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="generic">Genel (OPI / ECR ASCII)</option>
              <option value="ingenico">Ingenico</option>
              <option value="verifone">Verifone</option>
              <option value="pax">PAX</option>
              <option value="ynokc">Yeni Nesil ÖKC (Beko / Hugin / Profilo)</option>
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">Bağlantı Türü</Label>
            <select data-testid="bankpos-connection" value={dev.bankPos?.connection || "serial"} onChange={(e) => updBp("connection", e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="serial">Seri Port (WebSerial)</option>
              <option value="sim">Yalnız Simülasyon (test)</option>
            </select>
          </div>
          <div>
            <Label className="text-sm font-semibold text-slate-700">Baud Hızı (bps)</Label>
            <select data-testid="bankpos-baud" value={dev.bankPos?.baud || 9600} onChange={(e) => updBp("baud", Number(e.target.value))}
              className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green">
              {[9600, 19200, 38400, 115200].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <div className="flex w-full items-center justify-between rounded-md bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-200">
              <span className="text-sm font-medium text-slate-700">Simülasyon modu (test)</span>
              <Switch data-testid="bankpos-simulate" checked={dev.bankPos?.simulate !== false} onCheckedChange={(v) => updBp("simulate", v)} />
            </div>
          </div>
        </div>
        <div className="mt-4">
          {bankPosSupported() ? (
            <div className={`flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2 text-sm ring-1 ring-inset ${bp.connected ? "bg-emerald-50 ring-emerald-200" : "bg-slate-50 ring-slate-200"}`}>
              <span className="inline-flex items-center gap-2 font-semibold">
                <CreditCard className="h-4 w-4" />
                {bp.connected ? <span className="text-emerald-700" data-testid="bankpos-status">Cihaz bağlı</span> : <span className="text-slate-500" data-testid="bankpos-status">Cihaz bağlı değil {dev.bankPos?.simulate !== false ? "(simülasyon aktif)" : ""}</span>}
              </span>
              {bp.connected ? (
                <Button data-testid="bankpos-disconnect" onClick={() => disconnectBankPos()} size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">Bağlantıyı Kes</Button>
              ) : (
                <Button data-testid="bankpos-connect" onClick={async () => { try { await connectBankPos(dev.bankPos?.baud || 9600); toast.success("POS cihazına bağlanıldı"); } catch (e) { toast.error(e.message || "POS cihazı bağlanamadı"); } }} size="sm" className="bg-brand-green text-white hover:bg-brand-green-600"><CreditCard className="mr-1.5 h-4 w-4" /> Cihaza Bağlan (test)</Button>
              )}
            </div>
          ) : (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-[11px] text-amber-700 ring-1 ring-inset ring-amber-200">Bu tarayıcı seri port (WebSerial) desteklemiyor. Gerçek cihaz için Chrome/Edge kullanın; şimdilik Simülasyon modu çalışır.</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button data-testid="bankpos-save" onClick={save} className="bg-brand-green text-white hover:bg-brand-green-600"><Save className="mr-2 h-4 w-4" /> Kaydet</Button>
            <Button data-testid="bankpos-test-charge" onClick={testCharge} variant="outline" className="border-brand-orange text-brand-orange hover:bg-orange-50"><CreditCard className="mr-2 h-4 w-4" /> Test Ödeme (₺1,00 gönder)</Button>
          </div>
          {bpResult && (
            <div data-testid="bankpos-test-result" className={`mt-3 rounded-md px-3 py-2 text-sm ring-1 ring-inset ${bpResult.approved ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-red-50 text-red-700 ring-red-200"}`}>
              {bpResult.approved ? `✓ ${bpResult.message}` : `✕ ${bpResult.message}`}
              {bpResult.approved && <span className="ml-2 text-[11px] opacity-80">Ref: {bpResult.ref} · {bpResult.cardType} ****{bpResult.last4}{bpResult.simulated ? " · Simülasyon" : ""}</span>}
            </div>
          )}
        </div>
      </div>

      {/* Barkod okuyucu testi */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="overline mb-1">Barkod Okuyucu / El Terminali Testi</p>
        <p className="mb-3 text-xs text-slate-400">Bu sayfa açıkken (imleç bir kutuda değilken) kablosuz okuyucu veya el terminaliyle herhangi bir barkodu okutun.</p>
        <div className="flex items-center gap-4 rounded-xl bg-slate-900 p-5 text-white" data-testid="scan-test-box">
          <ScanLine className={`h-8 w-8 ${scanCount ? "text-emerald-400" : "text-slate-500"}`} />
          <div>
            <p className="text-[11px] uppercase tracking-widest text-slate-400">Son okunan barkod</p>
            <p data-testid="scan-test-value" className="font-mono text-2xl font-bold">{lastScan || "— okutun —"}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-[11px] uppercase tracking-widest text-slate-400">Okuma</p>
            <p data-testid="scan-test-count" className="text-2xl font-bold text-emerald-400">{scanCount}</p>
          </div>
        </div>
      </div>

      {/* Cihaz envanteri */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="overline mb-3">Bağlı Cihaz Envanteri (referans)</p>
        <div className="space-y-1">
          {FLEET.map(([name, count, Icon, note]) => (
            <div key={name} className="flex items-start gap-3 rounded-lg px-2 py-2 hover:bg-slate-50">
              <Icon className="mt-0.5 h-4 w-4 text-slate-500" />
              <div className="flex-1"><p className="text-sm font-semibold text-slate-800">{name} <span className="ml-1 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">{count} adet</span></p><p className="text-[11px] text-slate-400">{note}</p></div>
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-500" />
            </div>
          ))}
        </div>
      </div>

      {/* Kurulum rehberi */}
      <div className="rounded-2xl border border-sky-200 bg-sky-50 p-5" data-testid="setup-guide">
        <p className="flex items-center gap-1.5 font-heading text-sm font-bold text-sky-900"><Info className="h-4 w-4" /> Kurulum & Sessiz Baskı Rehberi</p>
        <ul className="mt-2 space-y-1.5 text-[13px] leading-relaxed text-sky-900/80">
          <li className="flex gap-2"><Usb className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span><b>Barkod okuyucu & el terminali:</b> ekstra kurulum gerekmez. Cihazlar klavye gibi davranır; uygulama okumaları POS ve Mal Kabul ekranlarında otomatik yakalar.</span></li>
          <li className="flex gap-2"><Printer className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span><b>Fiş & etiket yazıcı:</b> ilgili yazıcıyı bu bilgisayarda <b>varsayılan yazıcı</b> yapın. Tam otomatik (dialogsuz) baskı için Chrome'u <code>--kiosk-printing</code> ile başlatın veya QZ Tray köprüsü kurun (talep üzerine entegre edilir).</span></li>
          <li className="flex gap-2"><MonitorSmartphone className="mt-0.5 h-3.5 w-3.5 shrink-0" /> <span><b>Çoklu kasa:</b> her kasa/terminal bu sayfadan kendi adını girer; sepetler ve devam eden ödeme sunucuda tutulur, cihaz kapansa bile aynı yerden devam edilir.</span></li>
        </ul>
      </div>
      </div>
    </div>
  );
}
